import { RemoveCircle } from '@mui/icons-material';
import {
    DataGridPremium,
    GridActionsCellItem,
    GridValueFormatterParams,
    GridValueSetterParams,
} from '@mui/x-data-grid-premium';
import { selectedProductsContext } from 'pages/managePricing/managePricing';
import { useState } from 'react';
import { CustomToolbar } from 'shared/components';
import { useColumns } from 'shared/hooks';
import { GridColumns } from 'shared/types';
import { useSelectedItems } from 'shared/providers/provide-selected-items';
import { moneyFourDecimalFormatter } from 'shared/utility/dataGrid';
import { TAddProductsData } from 'types/backend';

export function SelectedProductsTable() {
    const [filterButtonEl, setFilterButtonEl] = useState<HTMLButtonElement | null>(null);

    const { selectedItems, updateItems } = useSelectedItems(selectedProductsContext);

    const handleClick = (params: TAddProductsData) => () => {
        updateItems(selectedItems.filter((selectedItem) => selectedItem.id !== params.id));
    };

    // valueSetter must stay pure: MUI v6 calls it while rendering the edit cell
    const productPriceSetter = (params: GridValueSetterParams<TAddProductsData, any>) => ({
        ...params.row,
        price: params.value,
    });

    const handleProcessRowUpdate = (updatedRow: TAddProductsData) => {
        updateItems(selectedItems.map((prod) => (prod.id === updatedRow.id ? updatedRow : prod)));
        return updatedRow;
    };

    const columnConfig: GridColumns<TAddProductsData> = [
        {
            headerName: 'Select Products',
            field: 'actions',
            type: 'actions',
            hide: false,
            filterable: false,
            pinnable: false,
            width: 200,
            getActions: (params) => [
                <GridActionsCellItem icon={<RemoveCircle />} label='Select' onClick={handleClick(params.row)} />,
            ],
        },
        {
            headerName: 'Product',
            field: 'itemid',
            hideable: false,
            pinnable: false,
            valueFormatter: (params: GridValueFormatterParams<number>) => `${params.value}`,
            width: 200,
        },
        {
            headerName: 'Region',
            field: 'region',
            hideable: false,
            pinnable: false,
            width: 200,
        },
        {
            headerName: 'Group',
            field: 'productcategoryname',
            hideable: false,
            pinnable: false,
            width: 200,
        },
        {
            headerName: 'Type',
            field: 'filmtype',
            hideable: false,
            pinnable: false,
            width: 100,
        },
        {
            headerName: 'Target Price',
            field: 'targetprice',
            hideable: false,
            pinnable: false,
            valueFormatter: moneyFourDecimalFormatter,
            type: 'number',
            filterable: false,
            width: 200,
        },
        {
            headerName: 'Market Price',
            field: 'price',
            hideable: false,
            pinnable: false,
            editable: true,
            valueFormatter: moneyFourDecimalFormatter,
            type: 'number',
            filterable: false,
            width: 200,
            valueSetter: productPriceSetter,
        },
    ];

    const columns = useColumns<TAddProductsData>({ columnConfig });

    return (
        <DataGridPremium
            autoHeight
            getRowId={(row) => row.id}
            columns={columns}
            rows={selectedItems}
            processRowUpdate={handleProcessRowUpdate}
            components={{
                Toolbar: CustomToolbar,
            }}
            componentsProps={{
                panel: {
                    anchorEl: filterButtonEl,
                    sx: {
                        '& .MuiDataGrid-filterFormColumnInput': {
                            width: 'auto',
                        },
                    },
                },
                toolbar: {
                    setFilterButtonEl,
                    hideExport: true,
                    title: 'Selected Products',
                },
            }}
        />
    );
}
